'use client';

import { motion } from 'framer-motion';
import { Edit3, Eye, Trash2, type LucideIcon } from 'lucide-react';

export const TABLE_ROW_ACTION_ICON_SIZE = 16;

const actionStyles = {
  view: 'bg-green-50 text-green-600 hover:bg-green-100 focus-visible:ring-green-200',
  edit: 'bg-blue-50 text-blue-600 hover:bg-blue-100 focus-visible:ring-blue-200',
  delete: 'bg-red-50 text-red-600 hover:bg-red-100 focus-visible:ring-red-200',
} as const;

type ActionVariant = keyof typeof actionStyles;

interface ActionIconButtonProps {
  variant: ActionVariant;
  label: string;
  onClick: () => void;
  icon: LucideIcon;
}

function ActionIconButton({ variant, label, onClick, icon: Icon }: ActionIconButtonProps) {
  return (
    <motion.button
      type="button"
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.92 }}
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`
        inline-flex shrink-0 items-center justify-center
        min-h-9 min-w-9 rounded-md p-2
        transition-colors focus-visible:outline-none focus-visible:ring-2
        ${actionStyles[variant]}
      `}
    >
      <Icon size={TABLE_ROW_ACTION_ICON_SIZE} strokeWidth={2} aria-hidden />
    </motion.button>
  );
}

export interface TableRowActionsProps {
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
}

/**
 * Standard View / Edit / Delete controls for admin data tables and mobile list cards.
 */
export function TableRowActions({ onView, onEdit, onDelete, className = '' }: TableRowActionsProps) {
  if (!onView && !onEdit && !onDelete) {
    return null;
  }

  return (
    <div className={`flex shrink-0 items-center gap-1.5 ${className}`.trim()}>
      {onView ? (
        <ActionIconButton variant="view" label="View" onClick={onView} icon={Eye} />
      ) : null}
      {onEdit ? (
        <ActionIconButton variant="edit" label="Edit" onClick={onEdit} icon={Edit3} />
      ) : null}
      {onDelete ? (
        <ActionIconButton variant="delete" label="Delete" onClick={onDelete} icon={Trash2} />
      ) : null}
    </div>
  );
}
